import { lazy, Suspense, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { ExternalLink } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { CheckCircle2, XCircle, Coins, Clock, Users, Lightbulb, FileText, Send, Handshake, Leaf, Sparkles, Mail, Phone } from 'lucide-react';
import logoDL from '@/assets/logo-dzialaj-lokalnie.webp';
import logoPAFW from '@/assets/logo-pafw.png';
import logoAkademia from '@/assets/logo-akademia.webp';
const DzialajLokalnieProjectsExplorer = lazy(() => import('./DzialajLokalnieProjectsExplorer'));
const DzialajLokalnieGallery = lazy(() => import('./DzialajLokalnieGallery'));
const DzialajLokalnieDocuments = lazy(() => import('./DzialajLokalnieDocuments'));

const steps = [
  { num: '1', title: 'Masz pomysł?', desc: 'Zgłoś się — doradzamy bezpłatnie.' },
  { num: '2', title: 'Złóż wniosek w naborze', desc: 'Nabór wiosną, śledź FB.' },
  { num: '3', title: 'Realizuj i rozlicz', desc: 'Towarzyszymy od umowy po sprawozdanie.' },
];

const municipalities = ['Lisewo', 'Grudziądz (wiej.)', 'Chełmża (wiej.)', 'Książki'];

const applySteps = [
  { icon: CheckCircle2, title: 'Sprawdź zasięg', desc: 'Twoja gmina musi być w obszarze działania SKIL: Lisewo, Grudziądz (wiej.), Chełmża (wiej.), Książki.' },
  { icon: Users, title: 'Skontaktuj się z nami', desc: 'Pomożemy doprecyzować pomysł i bezpłatnie skonsultujemy wniosek.' },
  { icon: FileText, title: 'Przygotuj wniosek', desc: 'Jasny cel, zaplanowane działania, mierzalne rezultaty i rozsądny budżet.' },
  { icon: Send, title: 'Złóż online', desc: 'Wniosek składasz przez generator wniosków.' },
];

const tematyczneSciezki = [
  {
    icon: Handshake,
    title: 'Dialog międzykulturowy w działaniu',
    desc: 'Dla społeczności lokalnych, które chcą budować relacje i wzajemne zrozumienie między mieszkańcami a uchodźcami z Ukrainy. Wspierane są projekty promujące dialog wielokulturowy, integrację oraz wspólne działanie ponad podziałami kulturowymi i językowymi.',
    examples: [
      'Wspólne warsztaty łączące różne społeczności',
      'Wydarzenia kulturalne integrujące mieszkańców i uchodźców',
      'Inicjatywy sąsiedzkie ponad podziałami',
    ],
  },
  {
    icon: Leaf,
    title: 'Działaj lokalnie i ekologicznie',
    desc: 'Dla tych, którym zależy na środowisku naturalnym i klimacie. Wspierane są projekty z zakresu edukacji ekologicznej, ochrony przyrody oraz lokalnych inicjatyw prośrodowiskowych.',
    examples: [
      'Warsztaty ekologiczne i edukacja klimatyczna',
      'Akcje sprzątania i ochrony przyrody',
      'Tworzenie ogrodów społecznych',
      'Edukacja o segregacji odpadów',
    ],
    note: 'Ścieżka realizowana we współpracy z WWF Polska.',
  },
  {
    icon: Sparkles,
    title: 'Młodzież działa lokalnie',
    desc: 'Dla projektów, w których główną rolę odgrywają młodzi dorośli w wieku 18–26 lat jako inicjatorzy i liderzy działań. W organizację projektu może być zaangażowana również młodzież od 13. roku życia.',
    examples: [
      'Projekty wymyślone i prowadzone przez młodych ludzi',
      'Inicjatywy aktywizujące rówieśników',
      'Działania na rzecz społeczności z młodzieżą w roli głównej',
    ],
  },
];

const faq = [
  {
    q: 'Czym jest grupa nieformalna?',
    a: 'Grupa nieformalna to minimum trzy osoby, które wspólnie realizują lub chcą realizować działania społeczne, ale nie mają osobowości prawnej. Mogą złożyć wniosek przez „patrona" (lokalną organizację z osobowością prawną) albo bezpośrednio do nas w ramach Inicjatywy Działaj Lokalnie.',
  },
  {
    q: 'Czym jest Inicjatywa Działaj Lokalnie?',
    a: 'To forma wsparcia dla grup nieformalnych, które nie mają patrona z osobowością prawną. SKIL nie przekazuje wtedy pieniędzy bezpośrednio, lecz sam ponosi koszty realizacji działań zgodnie z opisem projektu i budżetem. Podstawą współpracy jest umowa cywilnoprawna. W tej formie nie obowiązuje wymóg wkładu finansowego.',
  },
  {
    q: 'Ile wniosków mogę złożyć?',
    a: 'Liczba składanych wniosków nie jest ograniczona, ale komisja grantowa przyzna tylko jedną dotację danej grupie realizującej projekt. Organizacja może otrzymać dofinansowanie na własny projekt oraz dodatkowo na inicjatywy zgłoszone w imieniu różnych grup nieformalnych.',
  },
  {
    q: 'Co to jest „dobro wspólne"?',
    a: 'Dobro wspólne to wszystko, z czego korzystamy razem i za co jesteśmy współodpowiedzialni — czyste powietrze, bezpieczeństwo, park, miejsce spotkań, aktywność młodzieży. Projekt powinien tworzyć lub wzmacniać takie zasoby w Twojej społeczności.',
  },
  {
    q: 'Czy działania sportowe się kwalifikują?',
    a: 'Tak, ale tylko wtedy, gdy są częścią szerszego projektu aktywizującego społeczność i przyczyniają się do trwałych rezultatów. Sama seria treningów dla zawodników nie kwalifikuje się — w programie chodzi o budowanie wspólnoty, nie o samą rywalizację sportową.',
  },
  {
    q: 'Czy czynsz i koszty administracyjne mogą być wkładem własnym?',
    a: 'Tak, ale tylko proporcjonalnie do udziału projektu w kosztach stałych organizacji. Jeśli prowadzisz równolegle kilka projektów, podziel koszty (np. czynszu) między nie i wykaż część przypadającą na projekt grantowy jako wkład usługowy.',
  },
  {
    q: 'Czy mogę równolegle realizować inny projekt PAFW?',
    a: 'Tak. Grantobiorcy mogą prowadzić równolegle inne projekty finansowane przez Polsko-Amerykańską Fundację Wolności, pod warunkiem że środki z tych programów nie są wykazane jako wkład własny finansowy do projektu Działaj Lokalnie.',
  },
  {
    q: 'Jakie tematy mogą poruszać projekty?',
    a: 'Edukacja, kultura, sport (jako część aktywizacji), ekologia, ochrona zdrowia, pomoc społeczna, bezpieczeństwo, wsparcie osób z niepełnosprawnościami, wolontariat, ochrona dziedzictwa, promocja praw kobiet i wiele innych — wszystko, co wpisuje się w pożytek publiczny i służy lokalnej społeczności.',
  },
];

const DzialajLokalnieSection = () => {
  const [tab, setTab] = useState<'info' | 'projects' | 'strefa' | 'gallery' | 'documents'>('info');

  useEffect(() => {
    const map: Record<string, typeof tab> = {
      'dzialaj-lokalnie-strefa': 'strefa',
      'dzialaj-lokalnie-projekty': 'projects',
      'dzialaj-lokalnie-galeria': 'gallery',
      'dzialaj-lokalnie-dokumenty': 'documents',
    };
    const parseTabFromHash = () => {
      const raw = window.location.hash.replace('#', '');
      // Support /#dzialaj-lokalnie?tab=dokumenty
      const [hashId, queryPart] = raw.split('?');
      if (hashId === 'dzialaj-lokalnie') {
        const params = new URLSearchParams(queryPart || '');
        const tabParam = params.get('tab');
        const tabMap: Record<string, typeof tab> = {
          informacje: 'info',
          strefa: 'strefa',
          projekty: 'projects',
          galeria: 'gallery',
          dokumenty: 'documents',
        };
        if (tabParam && tabMap[tabParam]) {
          setTab(tabMap[tabParam]);
          document.getElementById('dzialaj-lokalnie')?.scrollIntoView({ behavior: 'smooth' });
          return;
        }
      }
      if (map[raw]) {
        setTab(map[raw]);
        document.getElementById('dzialaj-lokalnie')?.scrollIntoView({ behavior: 'smooth' });
      }
    };
    parseTabFromHash();
    window.addEventListener('hashchange', parseTabFromHash);
    window.addEventListener('popstate', parseTabFromHash);
    return () => {
      window.removeEventListener('hashchange', parseTabFromHash);
      window.removeEventListener('popstate', parseTabFromHash);
    };
  }, []);

  const { data: strefaDocs = [] } = useQuery<any[]>({
    queryKey: ['documents', 'strefa-grantobiorcy'],
    queryFn: async () => {
      const { data } = await supabase
        .from('documents')
        .select('*')
        .eq('category', 'strefa-grantobiorcy')
        .order('date', { ascending: false });
      return data || [];
    },
  });

  const tabBtnClass = (active: boolean) =>
    `relative snap-start shrink-0 px-5 py-2 rounded-full font-semibold text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-secondary ${
      active
        ? 'bg-background text-foreground shadow-sm after:content-[""] after:absolute after:left-4 after:right-4 after:-bottom-2 after:h-[3px] after:rounded-full after:bg-primary'
        : 'bg-secondary-foreground/20 text-secondary-foreground border border-secondary-foreground/30 hover:bg-secondary-foreground/30'
    }`;

  return (
    <section id="dzialaj-lokalnie" className="py-16 lg:py-24 bg-secondary text-secondary-foreground relative overflow-hidden">
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: 'radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0)',
        backgroundSize: '24px 24px'
      }} />
      <div className="container relative">
        <div className="relative">
          <div
            role="tablist"
            aria-label="Sekcje Działaj Lokalnie"
            className="flex gap-3 mb-12 overflow-x-auto snap-x snap-mandatory pb-2 -mx-4 px-4 lg:mx-0 lg:px-0 lg:flex-wrap lg:overflow-visible lg:snap-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            <button role="tab" aria-selected={tab === 'info'} onClick={() => setTab('info')} className={tabBtnClass(tab === 'info')}>
              Informacje o programie
            </button>
            <button role="tab" aria-selected={tab === 'strefa'} onClick={() => setTab('strefa')} className={tabBtnClass(tab === 'strefa')}>
              Strefa grantobiorcy
            </button>
            <button role="tab" aria-selected={tab === 'projects'} onClick={() => setTab('projects')} className={tabBtnClass(tab === 'projects')}>
              Projekty zrealizowane
            </button>
            <button role="tab" aria-selected={tab === 'gallery'} onClick={() => setTab('gallery')} className={tabBtnClass(tab === 'gallery')}>
              Galeria
            </button>
            <button role="tab" aria-selected={tab === 'documents'} onClick={() => setTab('documents')} className={tabBtnClass(tab === 'documents')}>
              Dokumenty
            </button>
          </div>
          <div
            aria-hidden="true"
            className="pointer-events-none absolute right-0 top-0 bottom-2 w-10 bg-gradient-to-l from-secondary to-transparent lg:hidden"
          />
        </div>


        {tab === 'info' && (
          <>
          <div className="grid lg:grid-cols-2 gap-10">
            <div>
              <h2 className="text-3xl lg:text-4xl font-heading font-bold mb-6">Twój pomysł zasługuje na wsparcie</h2>
              <p className="text-secondary-foreground font-bold mb-8 leading-relaxed">
                Jako Ośrodek Działaj Lokalnie od 2015 roku organizujemy konkurs grantowy we współpracy
                z Polsko-Amerykańską Fundacją Wolności i Akademią Rozwoju Filantropii w Polsce.
              </p>

              <div className="space-y-4 mb-8">
                {steps.map((s) => (
                  <div key={s.num} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-accent text-accent-foreground font-bold flex items-center justify-center flex-shrink-0 font-heading">
                      {s.num}
                    </div>
                    <div>
                      <p className="font-bold">{s.title}</p>
                      <p className="text-sm opacity-80">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-3">
                <Button asChild className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold">
                  <a href="https://generatorspoleczny.pl/" target="_blank" rel="noopener noreferrer">
                    Złóż wniosek — Generator Społeczny
                  </a>
                </Button>
                <Button asChild variant="outline" className="border-secondary-foreground/40 bg-transparent hover:bg-secondary-foreground/10 text-secondary-foreground">
                  <a href="#kontakt">Zapytaj o nabór</a>
                </Button>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-secondary-foreground/10 rounded-panel p-6">
                <p className="text-4xl font-bold font-heading text-accent">do 6 000 zł</p>
                <p className="text-sm opacity-80 mt-1">Kwota grantu na projekt (bez kwoty minimalnej)</p>
              </div>

              <div className="bg-secondary-foreground/10 rounded-panel p-6">
                <p className="font-bold mb-3">Gminy objęte programem:</p>
                <div className="flex flex-wrap gap-2">
                  {municipalities.map((m) => (
                    <span key={m} className="bg-background text-foreground text-sm px-3 py-1 rounded-full">{m}</span>
                  ))}
                </div>
              </div>

              <div className="bg-secondary-foreground/10 rounded-panel p-6">
                <p className="font-bold mb-2">Kto może aplikować?</p>
                <p className="text-sm opacity-80">
                  Organizacje pozarządowe, grupy nieformalne i inicjatywy lokalne z gmin Lisewo,
                  Grudziądz (wiejska), Chełmża (wiejska) i Książki.
                </p>
              </div>

              <div className="bg-background text-foreground rounded-panel p-6 text-center">
                <p className="font-heading font-bold mb-2">Nie wiesz od czego zacząć?</p>
                <Button className="bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold">
                  <a href="#kontakt">Skontaktuj się</a>
                </Button>
              </div>
            </div>
          </div>
          </>
        )}

        {tab === 'strefa' && (
          <div>
            <h2 className="text-3xl lg:text-4xl font-heading font-bold mb-3">Strefa grantobiorcy</h2>
            <p className="opacity-90 mb-10 max-w-3xl leading-relaxed">
              Praktyczny przewodnik po naborze: kto może aplikować, na co dostaniesz wsparcie, ile wynosi
              wkład własny i jak krok po kroku złożyć wniosek.
            </p>

            <div className="grid lg:grid-cols-2 gap-6 mb-10">
              {/* Kto może aplikować */}
              <div className="bg-secondary-foreground/10 rounded-panel p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Users className="w-6 h-6 text-accent" />
                  <h3 className="font-heading font-bold text-xl">Kto może aplikować</h3>
                </div>
                <ul className="space-y-2 text-sm">
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" /><span>Organizacje pozarządowe (stowarzyszenia, fundacje) z gmin Lisewo, Grudziądz (wiej.), Chełmża (wiej.), Książki.</span></li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" /><span>Grupy nieformalne — minimum 3 osoby, samodzielnie lub z patronem.</span></li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" /><span>Lokalne instytucje działające w sferze pożytku publicznego.</span></li>
                </ul>
                <div className="mt-4 pt-4 border-t border-secondary-foreground/20">
                  <p className="text-xs font-semibold opacity-80 mb-2">Kto NIE może aplikować:</p>
                  <ul className="space-y-1 text-xs opacity-80">
                    <li className="flex gap-2"><XCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /><span>Fundacje skarbu państwa, partii politycznych</span></li>
                    <li className="flex gap-2"><XCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /><span>LGD, LGR, LOT, parafie i jednostki kościołów</span></li>
                  </ul>
                </div>
              </div>

              {/* Na co można dostać */}
              <div className="bg-secondary-foreground/10 rounded-panel p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Lightbulb className="w-6 h-6 text-accent" />
                  <h3 className="font-heading font-bold text-xl">Na co można dostać dotację</h3>
                </div>
                <p className="text-sm opacity-90 mb-3">
                  Projekty, które aktywizują społeczność wokół dobra wspólnego i mają jasny cel,
                  mierzalne rezultaty oraz rozsądny budżet.
                </p>
                <div className="flex flex-wrap gap-2">
                  {['Kultura', 'Edukacja', 'Ekologia', 'Zdrowie', 'Pomoc społeczna', 'Bezpieczeństwo', 'Wolontariat', 'Dziedzictwo', 'Sport (jako aktywizacja)'].map((t) => (
                    <span key={t} className="bg-background text-foreground text-xs px-3 py-1 rounded-full">{t}</span>
                  ))}
                </div>
              </div>

              {/* Kwota i czas */}
              <div className="bg-secondary-foreground/10 rounded-panel p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Coins className="w-6 h-6 text-accent" />
                  <h3 className="font-heading font-bold text-xl">Kwota i czas trwania</h3>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-3xl font-bold font-heading text-accent">do 6 000 zł</p>
                    <p className="text-xs opacity-80">bez kwoty minimalnej</p>
                  </div>
                  <div className="pt-3 border-t border-secondary-foreground/20 flex items-center gap-3">
                    <Clock className="w-5 h-5 text-accent flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-sm">Czas realizacji: 3–6 miesięcy</p>
                      <p className="text-xs opacity-80">Projekt nie może być jednorazową akcją.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Wkład własny */}
              <div className="bg-secondary-foreground/10 rounded-panel p-6">
                <div className="flex items-center gap-3 mb-4">
                  <CheckCircle2 className="w-6 h-6 text-accent" />
                  <h3 className="font-heading font-bold text-xl">Wkład własny</h3>
                </div>
                <p className="text-sm opacity-90 mb-3">
                  Wymagane <strong>25% kwoty dotacji</strong>, w tym minimum <strong>5% wkładu finansowego</strong>.
                  Dla Inicjatyw Działaj Lokalnie (grupy nieformalne bez patrona) wymóg finansowy nie obowiązuje.
                </p>
                <ul className="text-xs space-y-1.5 opacity-90">
                  <li>• <strong>Finansowy</strong> — środki na koncie organizacji</li>
                  <li>• <strong>Rzeczowy</strong> — materiały, sprzęt, produkty</li>
                  <li>• <strong>Usługowy</strong> — nieodpłatne usługi (transport, catering, sala)</li>
                  <li>• <strong>Wolontariat</strong> — praca wyceniona po stawkach rynkowych</li>
                </ul>
              </div>
            </div>

            {/* Ścieżki tematyczne 2026 */}
            <div className="mb-10">
              <h3 className="font-heading font-bold text-2xl mb-3">
                Ścieżki tematyczne w programie Działaj Lokalnie 2026
              </h3>
              <p className="opacity-90 mb-6 max-w-3xl leading-relaxed">
                W edycji 2026 funkcjonują trzy ogólnopolskie ścieżki tematyczne. Realizacja projektu w ramach
                jednej z nich jest <strong>dobrowolna</strong>, ale może przynieść <strong>do 5 dodatkowych punktów</strong>{' '}
                podczas oceny merytorycznej wniosku.
              </p>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                {tematyczneSciezki.map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <div key={i} className="bg-secondary-foreground/10 rounded-panel p-6 flex flex-col">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-11 h-11 rounded-full bg-accent/20 text-accent flex items-center justify-center flex-shrink-0">
                          <Icon className="w-6 h-6" />
                        </div>
                        <h4 className="font-heading font-bold text-lg leading-tight">{s.title}</h4>
                      </div>
                      <p className="text-sm opacity-90 mb-4 leading-relaxed">{s.desc}</p>
                      <p className="text-xs font-semibold opacity-80 mb-2 uppercase tracking-wide">Przykładowe działania</p>
                      <ul className="space-y-1.5 text-sm mb-3">
                        {s.examples.map((ex, j) => (
                          <li key={j} className="flex gap-2">
                            <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                            <span>{ex}</span>
                          </li>
                        ))}
                      </ul>
                      {s.note && (
                        <p className="text-xs opacity-80 italic mt-auto pt-3 border-t border-secondary-foreground/20">
                          {s.note}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="bg-accent/10 border border-accent/30 rounded-panel p-5 flex gap-4 items-start">
                <Lightbulb className="w-6 h-6 text-accent flex-shrink-0 mt-0.5" />
                <div className="text-sm leading-relaxed">
                  <p className="font-bold mb-1">Czy muszę wybrać ścieżkę tematyczną?</p>
                  <p className="opacity-90">
                    Nie — wybór jest dobrowolny. Jeśli Twój projekt nie wpisuje się w żadną z nich, we wniosku
                    zaznaczasz po prostu <em>„nie dotyczy"</em>. Projekty spoza ścieżek są równoprawnie oceniane
                    pod względem formalnym. Wybór ścieżki może jednak przynieść <strong>do 5 dodatkowych punktów</strong>{' '}
                    podczas oceny merytorycznej.
                  </p>
                </div>
              </div>

              <div className="mt-5 text-xs opacity-80 leading-relaxed">
                <p className="mb-1">
                  <strong>Masz pytania?</strong> Skontaktuj się z nami:{' '}
                  <a href="mailto:kuznialisewo@gmail.com" className="inline-flex items-center gap-1 underline hover:text-accent">
                    <Mail className="w-3.5 h-3.5" /> kuznialisewo@gmail.com
                  </a>{' '}·{' '}
                  <a href="tel:+48531982775" className="inline-flex items-center gap-1 underline hover:text-accent">
                    <Phone className="w-3.5 h-3.5" /> 531 982 775
                  </a>{' '}
                  (Bartłomiej Kochowicz, koordynator)
                </p>
                <p>
                  Informacje dotyczą edycji 2026 programu Działaj Lokalnie realizowanego przez Akademię Rozwoju
                  Filantropii w Polsce ze środków Polsko-Amerykańskiej Fundacji Wolności.
                </p>
              </div>
            </div>

            {/* Jak złożyć wniosek */}
            <div className="bg-background text-foreground rounded-panel p-6 lg:p-8 mb-10">
              <h3 className="font-heading font-bold text-2xl mb-6">Jak złożyć wniosek — 4 kroki</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {applySteps.map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <div key={i} className="relative">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center flex-shrink-0">
                          <Icon className="w-5 h-5" />
                        </div>
                        <span className="font-heading font-bold text-2xl text-accent">{i + 1}</span>
                      </div>
                      <p className="font-bold mb-1">{s.title}</p>
                      <p className="text-sm text-muted-foreground">{s.desc}</p>
                    </div>
                  );
                })}
              </div>
              <div className="mt-6 pt-6 border-t flex flex-wrap gap-3">
                <Button asChild className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold">
                  <a href="https://generatorspoleczny.pl/" target="_blank" rel="noopener noreferrer">Złóż wniosek o grant</a>
                </Button>
                <Button asChild variant="outline">
                  <a href="#kontakt">Mam pytanie</a>
                </Button>
              </div>
            </div>

            {/* Linki i materiały (CMS) */}
            {strefaDocs.length > 0 && (
              <div className="mb-10">
                <h3 className="font-heading font-bold text-2xl mb-4">Materiały i linki dla grantobiorców</h3>
                <ul className="grid sm:grid-cols-2 gap-3">
                  {strefaDocs.map((d) => (
                    <li key={d.id}>
                      <a
                        href={d.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-start gap-3 bg-secondary-foreground/10 hover:bg-secondary-foreground/20 transition-colors rounded-panel p-4"
                      >
                        <ExternalLink className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden="true" />
                        <div className="min-w-0">
                          <p className="font-semibold leading-tight">{d.title}</p>
                          {d.description && <p className="text-xs opacity-80 mt-1">{d.description}</p>}
                        </div>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* FAQ */}
            <div>
              <h3 className="font-heading font-bold text-2xl mb-6">Najczęstsze pytania</h3>
              <Accordion type="single" collapsible className="bg-secondary-foreground/10 rounded-panel px-6">
                {faq.map((item, i) => (
                  <AccordionItem key={i} value={`q-${i}`} className="border-secondary-foreground/20">
                    <AccordionTrigger className="text-left font-semibold hover:no-underline">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="opacity-90 leading-relaxed">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
              <p className="text-xs opacity-90 mt-4">
                Pełne zasady programu znajdziesz na{' '}
                <a href="https://dzialajlokalnie.pl/zasady-programu/" target="_blank" rel="noopener noreferrer" className="underline hover:text-accent">
                  dzialajlokalnie.pl/zasady-programu
                </a>.
              </p>
            </div>
          </div>
        )}

        {tab === 'projects' && (
          <Suspense fallback={<div className="py-12 text-center text-sm opacity-90">Ładowanie projektów…</div>}>
            <DzialajLokalnieProjectsExplorer />
          </Suspense>
        )}

        {tab === 'gallery' && (
          <Suspense fallback={<div className="py-12 text-center text-sm opacity-90">Ładowanie galerii…</div>}>
            <DzialajLokalnieGallery />
          </Suspense>
        )}

        {tab === 'documents' && (
          <Suspense fallback={<div className="py-12 text-center text-sm opacity-90">Ładowanie dokumentów…</div>}>
            <DzialajLokalnieDocuments />
          </Suspense>
        )}

        <div className="mt-12 pt-8 border-t border-secondary-foreground/20">
          <p className="text-sm opacity-90 mb-4">We współpracy z:</p>
          <div className="flex flex-wrap items-center gap-6">
            {[
              { name: 'Polsko-Amerykańska Fundacja Wolności', url: 'https://www.pafw.pl', logo: logoPAFW },
              { name: 'Akademia Rozwoju Filantropii w Polsce', url: 'https://www.filantropia.org.pl', logo: logoAkademia },
              { name: 'Działaj Lokalnie', url: 'https://www.dzialajlokalnie.pl', logo: logoDL },
            ].map((p) => (
              <a
                key={p.name}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-background/10 hover:bg-background/20 rounded-full px-4 py-2 transition-colors group"
              >
                <img src={p.logo} alt={p.name} width={140} height={28} className="h-7 w-auto max-w-[140px] object-contain bg-background rounded-sm p-0.5" loading="lazy" />
                <span className="text-sm font-semibold group-hover:underline">{p.name}</span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DzialajLokalnieSection;
